import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import ModalInTailwind from '$components/ModalInTailwind/ModalInTailwind';
import {
  InputSwitch,
  InputSwitchPassThroughMethodOptions,
} from 'primereact/inputswitch';
import { useUploadCandidateInfoMutation } from '$store/modules/candidates';
import classes from './SkillsEditableContainer.module.css';
import SkillsReadOnly from './SkillsReadOnly';
import SkillsContainer from '../SkillsFormContainer/SkillsContainer';
import NewSkillsContainer from '../SkillsFormContainer/NewSkillsContainer';
import EditableContainerInternal from '../EditableContainer/EditableContainerInternal';
import useInternalEditableContainer from '../../hooks/useInternalEditableContainer';
import { useGetCandidateSkillsQuery } from '../../store/modules/candidate-skill';
import CardSkeleton from '../Positions/CandidateSummaryCardList/CardSkeleton/CardSkeleton';

interface Props {
  isActive: boolean;
  secondary: boolean;
  candidateIsSimeraAICertified: boolean;
}

const SkillsEditableContainerInternal: FC<Props> = ({
  isActive,
  secondary,
  candidateIsSimeraAICertified,
}) => {
  const [t, i18next] = useTranslation('global');
  const { candidateId } = useParams();

  const [showSimeraAiCertifiedModal, setShowSimeraAiCertifiedModal] =
    useState(false);
  const [isSimeraAICertified, setIsSimeraAICertified] = useState(
    candidateIsSimeraAICertified
  );

  const { data, isLoading } = useGetCandidateSkillsQuery({
    candidateId: +candidateId!,
    lang: i18next.language as 'en' | 'es',
  });
  const [uploadCandidateInfo] = useUploadCandidateInfoMutation();

  const {
    newFormStep: newSkills,
    isEdit,
    setIsEdit,
    handleCancel,
    handleCancelModal,
    show,
    onClickSave,
    onClickPencil,
    onClickPlus,
  } = useInternalEditableContainer();

  const newSkillsOrField = () => {
    if (isLoading)
      return <CardSkeleton hasProfilePicture={false} rows={2} padding={0} />;

    if (newSkills) {
      return (
        <NewSkillsContainer
          onClickCancel={handleCancel}
          isEdit
          setEdit={setIsEdit}
          onClickCancelModal={handleCancelModal}
          show={show}
          onClickSave={onClickSave}
          newSkills
          countForms={data?.length || 1}
        />
      );
    }
    return (
      <div
        className={`grid grid-cols-1 md:grid-cols-2 gap-2 ${classes.SkillsEditableContainer}`}
      >
        {data &&
          data?.map((skill, idx) => (
            <SkillsReadOnly
              skillsLength={data?.length ?? 0}
              key={skill?.Id}
              skill={skill}
              idx={idx}
              secondary={secondary}
            />
          ))}
      </div>
    );
  };

  const confirmChangeSimeraAiCertifiedHandler = async () => {
    await uploadCandidateInfo({
      candidateId: +candidateId! as number,
      data: { IsSimeraAICertified: !isSimeraAICertified },
    });
    setIsSimeraAICertified((prev) => !prev);
    setShowSimeraAiCertifiedModal(false);
  };

  return (
    <EditableContainerInternal
      title={t('formNames.skills')}
      isEditable={secondary && isEdit}
      data-testid="skills-editableForm"
      onClickPencil={onClickPencil}
      onClickPlus={onClickPlus}
      hasAddMore
      addMore={newSkills ? 'Skill' : false}
      isActive={isActive}
    >
      {!isEdit ? (
        <SkillsContainer
          isEdit
          setEdit={setIsEdit}
          onClickCancel={handleCancel}
          onClickCancelModal={handleCancelModal}
          show={show}
          secondary
        />
      ) : (
        <>
          {secondary && !newSkills && (
            <div className="flex flex-row gap-8 items-center my-4">
              <span>Simera AI Certified</span>
              <InputSwitch
                tooltip={`${
                  isSimeraAICertified ? 'Remove' : 'Add'
                } Simera AI Certified skill`}
                pt={{
                  slider: ({ props }: InputSwitchPassThroughMethodOptions) => ({
                    className: `${
                      // eslint-disable-next-line react/prop-types
                      props.checked && 'bg-internal-main/50'
                    }`,
                  }),
                }}
                checked={!!isSimeraAICertified}
                onChange={() => setShowSimeraAiCertifiedModal((prev) => !prev)}
              />
            </div>
          )}
          {newSkillsOrField()}
          {secondary && (
            <ModalInTailwind
              isOpen={showSimeraAiCertifiedModal}
              closeModal={() => setShowSimeraAiCertifiedModal(false)}
              blurBackground
            >
              <div className="bg-white rounded p-8">
                <h3 className="font-semibold text-xl mb-4">
                  {`${
                    isSimeraAICertified ? 'Remove' : 'Add'
                  } Simera AI Certified tag?`}
                </h3>
                <h1 className="mb-2">
                  Are you sure you want to{' '}
                  {`${isSimeraAICertified ? 'remove' : 'add'}`} Simera AI
                  Certified tag to this candidate?
                </h1>
                <div className="flex flex-row gap-4 justify-end mt-8">
                  <button
                    type="button"
                    className="rounded bg-gray-400 px-4 py-2 text-white"
                    onClick={() => setShowSimeraAiCertifiedModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={confirmChangeSimeraAiCertifiedHandler}
                    className="bg-internal-main rounded px-4 py-2 text-white"
                  >
                    Accept
                  </button>
                </div>
              </div>
            </ModalInTailwind>
          )}
        </>
      )}
    </EditableContainerInternal>
  );
};

export default SkillsEditableContainerInternal;
